import { Cloudinary } from '@cloudinary/url-gen';
import { dpr, format, quality } from '@cloudinary/url-gen/actions/delivery';
import { crop, fill, limitFill } from '@cloudinary/url-gen/actions/resize';
import { face } from '@cloudinary/url-gen/qualifiers/focusOn';
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format';
import { autoGravity, focusOn } from '@cloudinary/url-gen/qualifiers/gravity';
import { auto as autoQuality } from '@cloudinary/url-gen/qualifiers/quality';
const cl = new Cloudinary({
    cloud: {
        cloudName: 'essential-living',
    },
    url: {
        secure: true,
    },
});
/**
 * Create an image URL for ImageProcessor
 * @param image The image model
 * @param imageParams The image parameters
 * @returns The image URL
 */
export function imageUrl(image, imageParams) {
    if (!image?.src) {
        return '';
    }
    let clImage = cl.image(image.src);
    const resizeAction = getResizeAction(image, imageParams);
    if (resizeAction) {
        clImage = clImage.resize(resizeAction);
    }
    clImage = clImage.delivery(getFormatAction(imageParams)).delivery(quality(autoQuality()));
    if (imageParams.dpr) {
        clImage = clImage.delivery(dpr(imageParams.dpr));
    }
    // Set version to enable mass invalidation.
    clImage.setVersion(process.env.CLOUDINARY_VERSION ?? '1');
    return clImage.toURL();
}
export function fileDownloadUrl(image) {
    if (!image?.src) {
        return '';
    }
    const clImage = cl.image(image.src);
    clImage.addFlag('attachment');
    return clImage.toURL();
}
function getResizeAction(image, imageParams) {
    if (!imageParams.width && !imageParams.height) {
        console.warn(`Image Not Resized: "${image.src}"`, image, imageParams);
        return undefined;
    }
    let resizeAction;
    switch (imageParams.crop) {
        case 'crop':
            resizeAction = crop();
            break;
        case 'fill':
            resizeAction = fill();
            break;
        case 'lfill':
            resizeAction = limitFill();
            break;
        default:
            resizeAction = fill();
            break;
    }
    if (imageParams.width) {
        resizeAction = resizeAction.width(imageParams.width);
    }
    else if (imageParams.widthratio) {
        resizeAction = resizeAction.aspectRatio(imageParams.widthratio);
    }
    if (imageParams.height) {
        resizeAction = resizeAction.height(imageParams.height);
    }
    else if (imageParams.heightratio) {
        resizeAction = resizeAction.aspectRatio(imageParams.heightratio);
    }
    if (imageParams.gravity === 'face') {
        resizeAction = resizeAction.gravity(focusOn(face()));
    }
    else {
        resizeAction = resizeAction.gravity(autoGravity());
    }
    return resizeAction;
}
function getFormatAction(imageParams) {
    let formatAction;
    if (imageParams.format) {
        formatAction = format(imageParams.format);
    }
    else {
        formatAction = format(autoFormat());
    }
    return formatAction;
}
export function getSrcs(image, upper, lower, steps, ratio) {
    const sizes = getRange(upper, lower, steps);
    return sizes.map((size) => `${imageUrl(image, { width: size, heightratio: ratio })} ${size}w`);
}
export function getRetinaSrcs(image, width, ratio) {
    const dprs = [1, 1.5, 2, 2.5, 3];
    return dprs.map((dpr) => `${imageUrl(image, { width: width, heightratio: ratio, dpr })} ${dpr}x`);
}
function getRange(upper, lower, steps) {
    const difference = upper - lower;
    const increment = difference / (steps - 1);
    return [
        lower,
        ...Array(steps - 2)
            .fill('')
            .map((_, index) => Math.ceil(lower + increment * (index + 1))),
        upper,
    ];
}
const imgSrcRegex = /<img[^>]*(src="[^"]+")[^>]*>/g;
const cloudinaryPathRegex = /res.cloudinary.com\/[^/]*\/image\/upload\/v[\d]*\/(.*)"/;
export function transformCloudinarySrcsInHtmlString(HtmlString, imageParams, upper, lower, steps) {
    HtmlString = HtmlString.replace(/\s+/g, ' ').trim();
    const images = HtmlString.matchAll(imgSrcRegex);
    let ratio = undefined;
    if (imageParams.heightratio && imageParams.widthratio) {
        ratio = imageParams.heightratio / imageParams.widthratio;
    }
    if (images) {
        Array.from(images).forEach((image) => {
            const cloudinaryPath = image[1].match(cloudinaryPathRegex);
            if (cloudinaryPath && cloudinaryPath[1] && image[0].indexOf('srcset') == -1) {
                const decodedPath = decodeURI(cloudinaryPath[1]);
                const src = imageUrl({ src: decodedPath }, imageParams);
                const srcSet = getSrcs({ src: decodedPath }, upper, lower, steps, ratio).join(',');
                HtmlString = HtmlString.replace(image[1], `src="${src}" srcset="${srcSet}"`);
            }
        });
    }
    return HtmlString;
}
